import type { LegacyCaseDetail, LegacyPatientData } from "../types/legacyCases";

export type LegacyPatientCandidate =
  LegacyCaseDetail["patientCandidates"][number];

export function legacyPatientIdentity(
  patient: LegacyPatientData
): LegacyPatientData {
  return {
    firstName: patient.firstName,
    lastName: patient.lastName,
    dateOfBirth: patient.dateOfBirth,
    sex: patient.sex,
    medicalRecordNumber: patient.medicalRecordNumber || undefined,
  };
}

export function sameLegacyPatientIdentity(
  reviewed: LegacyPatientData | undefined,
  current: LegacyPatientData | undefined
): boolean {
  return Boolean(
    reviewed &&
      current &&
      reviewed.firstName === current.firstName &&
      reviewed.lastName === current.lastName &&
      reviewed.dateOfBirth === current.dateOfBirth &&
      reviewed.sex === current.sex &&
      (reviewed.medicalRecordNumber || "") ===
        (current.medicalRecordNumber || "")
  );
}

export function mergeLegacyPatientCandidates(
  detail: LegacyPatientCandidate[],
  search: LegacyPatientCandidate[],
  retained: LegacyPatientCandidate | undefined,
  searchIsNewer: boolean
): LegacyPatientCandidate[] {
  const responses = searchIsNewer
    ? [...detail, ...search]
    : [...search, ...detail];
  return Array.from(
    new Map(
      [...(retained ? [retained] : []), ...responses].map((patient) => [
        patient.id,
        patient,
      ])
    ).values()
  );
}

export function legacyPatientSourceChange(
  currentValue: string,
  nextValue: string
): {
  patientReviewedIdentity?: undefined;
  patientReviewedCandidateIds?: undefined;
} {
  return currentValue === nextValue
    ? {}
    : {
        patientReviewedIdentity: undefined,
        patientReviewedCandidateIds: undefined,
      };
}

export function reviewedLegacyPatientCandidates(
  reviewedIds: string[] | undefined,
  candidates: LegacyPatientCandidate[]
): boolean {
  if (!reviewedIds) return false;
  const current = new Set(candidates.map((patient) => patient.id));
  const reviewed = new Set(reviewedIds);
  return (
    current.size === reviewed.size &&
    Array.from(current).every((id) => reviewed.has(id))
  );
}
